import { useEffect, useRef, useState } from "react";
import {
  Loader2, Plus, Pencil, Trash2, ArrowUp, ArrowDown, Megaphone, UploadCloud, X, CalendarDays, MousePointerClick, RotateCcw,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "./adminApi";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import { imgUrl } from "../lib/spa";

const inp =
  "w-full rounded-lg border border-white/15 bg-black/40 px-3 py-2.5 text-sm text-white outline-none transition-colors focus:border-gold";
const lbl = "mb-1.5 block text-xs uppercase tracking-[0.2em] text-white/50";

const PLACEMENTS = [
  { key: "home", label: "Faixa na página inicial" },
  { key: "topbar", label: "Barra no topo (todas as páginas)" },
  { key: "popup", label: "Pop-up ao entrar" },
];
const placementLabel = { home: "Página inicial", topbar: "Barra topo", popup: "Pop-up" };

const todayLisbon = () =>
  new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Lisbon" }).format(new Date());
const fmtDate = (d) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString("pt-PT") : "");

const statusOf = (b) => {
  if (!b.active) return { text: "Desativado", cls: "bg-white/10 text-white/50" };
  const t = todayLisbon();
  if (b.starts_at && b.starts_at > t) return { text: `Agendado (${fmtDate(b.starts_at)})`, cls: "bg-sky-500/15 text-sky-300" };
  if (b.ends_at && b.ends_at < t) return { text: "Expirado", cls: "bg-red-500/15 text-red-300" };
  return { text: "No ar", cls: "bg-emerald-500/15 text-emerald-300" };
};

const EMPTY = {
  title: "",
  message: "",
  button_label: "Saber mais",
  whatsapp_message: "",
  image: "",
  image_mobile: "",
  show_text: true,
  placements: ["home"],
  active: true,
  starts_at: "",
  ends_at: "",
};

const BannerImageUpload = ({ value, onChange, testId = "banner-image", hint = "JPG, PNG ou WEBP · máx. 8 MB (opcional)" }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);

  const handle = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Apenas ficheiros de imagem");
    if (file.size > 8 * 1024 * 1024) return toast.error("Ficheiro demasiado grande (máx. 8 MB)");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Falha no upload");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <input
        ref={ref}
        type="file"
        accept="image/*"
        className="hidden"
        data-testid={`${testId}-input`}
        onChange={async (e) => {
          await handle(e.target.files?.[0]);
          e.target.value = "";
        }}
      />
      <div
        role="button"
        tabIndex={0}
        data-testid={`${testId}-dropzone`}
        onClick={() => !busy && ref.current?.click()}
        onKeyDown={(e) => e.key === "Enter" && ref.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDrag(true);
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={async (e) => {
          e.preventDefault();
          setDrag(false);
          await handle(e.dataTransfer.files?.[0]);
        }}
        className={`relative flex min-h-[160px] cursor-pointer items-center justify-center overflow-hidden rounded-xl border border-dashed transition-colors ${
          drag ? "border-gold bg-gold/10" : "border-white/20 bg-black/30 hover:border-gold/60"
        }`}
      >
        {value ? (
          <img src={imgUrl(value)} alt="" className="max-h-64 w-full object-contain" />
        ) : busy ? (
          <Loader2 className="h-6 w-6 animate-spin text-gold" />
        ) : (
          <div className="flex flex-col items-center gap-2 p-6 text-center text-white/45">
            <UploadCloud className="h-7 w-7" strokeWidth={1.3} />
            <span className="text-xs uppercase tracking-widest">Arraste a imagem ou clique</span>
            <span className="text-[11px] normal-case tracking-normal text-white/35">{hint}</span>
          </div>
        )}
        {busy && value && (
          <span className="absolute inset-0 flex items-center justify-center bg-black/60">
            <Loader2 className="h-6 w-6 animate-spin text-gold" />
          </span>
        )}
      </div>
      {value && (
        <button
          type="button"
          data-testid={`${testId}-remove`}
          onClick={() => onChange("")}
          className="mt-2 inline-flex items-center gap-1 text-xs text-red-300/80 transition hover:text-red-200"
        >
          <X className="h-3.5 w-3.5" /> Remover imagem
        </button>
      )}
    </div>
  );
};

const BannerForm = ({ banner, onClose, onSaved }) => {
  const isNew = !banner?.id;
  const [form, setForm] = useState(() => ({ ...EMPTY, ...(banner || {}) }));
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const togglePlacement = (k) =>
    set("placements", form.placements.includes(k) ? form.placements.filter((p) => p !== k) : [...form.placements, k]);

  const save = async (e) => {
    e.preventDefault();
    if (!form.placements.length) return toast.error("Escolha pelo menos um local");
    if (!form.title.trim() && !form.message.trim() && !form.image) return toast.error("Indique um texto ou uma imagem");
    setBusy(true);
    const payload = {
      title: form.title,
      message: form.message,
      button_label: form.button_label,
      whatsapp_message: form.whatsapp_message,
      image: form.image,
      image_mobile: form.image_mobile,
      show_text: form.show_text,
      placements: form.placements,
      active: form.active,
      starts_at: form.starts_at || "",
      ends_at: form.ends_at || "",
    };
    try {
      if (isNew) await api.post("/admin/banners", payload);
      else await api.put(`/admin/banners/${banner.id}`, payload);
      toast.success(isNew ? "Banner criado" : "Banner guardado");
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erro ao guardar");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="banner-form"
        aria-describedby={undefined}
        className="max-h-[90vh] max-w-2xl overflow-y-auto border-white/10 bg-[#101010] text-white"
      >
        <DialogTitle className="font-display text-2xl font-medium">{isNew ? "Novo banner" : "Editar banner"}</DialogTitle>
        <form onSubmit={save} className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-5">
            <div className="sm:col-span-3">
              <label className={lbl}>Imagem horizontal (computador)</label>
              <BannerImageUpload
                value={form.image}
                onChange={(v) => set("image", v)}
                testId="banner-image"
                hint="Ideal 1600×1000 · JPG/PNG/WEBP · máx. 8 MB"
              />
            </div>
            <div className="sm:col-span-2">
              <label className={lbl}>Imagem quadrada (telemóvel e pop-up)</label>
              <BannerImageUpload
                value={form.image_mobile}
                onChange={(v) => set("image_mobile", v)}
                testId="banner-image-mobile"
                hint="Ideal 1080×1080 · opcional"
              />
            </div>
          </div>
          {(form.image || form.image_mobile) && (
            <label className="flex items-start gap-3 rounded-xl border border-gold/20 bg-gold/[0.05] p-4 text-sm text-white/80">
              <Switch
                data-testid="banner-image-only"
                checked={!form.show_text}
                onCheckedChange={(v) => set("show_text", !v)}
              />
              <span>
                O texto já está na imagem
                <span className="block text-xs text-white/45">
                  Mostra só a imagem + botão &quot;Saber mais&quot;. O título e a mensagem abaixo continuam a ser usados na barra do topo e no WhatsApp.
                </span>
              </span>
            </label>
          )}
          <div>
            <label className={lbl}>Título</label>
            <input data-testid="banner-title" className={inp} value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Ex.: Promoção Exclusiva" />
          </div>
          <div>
            <label className={lbl}>Mensagem da promoção</label>
            <textarea
              data-testid="banner-message"
              rows={3}
              className={inp}
              value={form.message}
              onChange={(e) => set("message", e.target.value)}
              placeholder="Ex.: Faça 2 massagens de 1h e receba grátis uma massagem de 30 minutos."
            />
            <p className="mt-1 text-[11px] text-white/40">Se só carregar uma imagem, o título e a mensagem podem ficar vazios.</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <label className={lbl}>Texto do botão</label>
              <input data-testid="banner-button-label" className={inp} value={form.button_label} onChange={(e) => set("button_label", e.target.value)} />
            </div>
            <div className="sm:col-span-2">
              <label className={lbl}>Mensagem enviada no WhatsApp</label>
              <input
                data-testid="banner-whatsapp-message"
                className={inp}
                value={form.whatsapp_message}
                onChange={(e) => set("whatsapp_message", e.target.value)}
                placeholder="Vazio = «Olá! Gostaria de saber mais sobre a promoção: …»"
              />
            </div>
          </div>
          <div>
            <label className={lbl}>Onde mostrar</label>
            <div className="flex flex-wrap gap-2">
              {PLACEMENTS.map((p) => {
                const on = form.placements.includes(p.key);
                return (
                  <button
                    type="button"
                    key={p.key}
                    data-testid={`banner-placement-${p.key}`}
                    onClick={() => togglePlacement(p.key)}
                    className={`rounded-full border px-4 py-2 text-xs transition ${
                      on ? "border-gold bg-gold/15 text-gold" : "border-white/15 text-white/55 hover:border-white/40"
                    }`}
                  >
                    {on ? "✓ " : ""}
                    {p.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className={lbl}>Data de início (opcional)</label>
              <input type="date" data-testid="banner-starts-at" className={`${inp} [color-scheme:dark]`} value={form.starts_at} onChange={(e) => set("starts_at", e.target.value)} />
            </div>
            <div>
              <label className={lbl}>Data de fim (opcional)</label>
              <input type="date" data-testid="banner-ends-at" className={`${inp} [color-scheme:dark]`} value={form.ends_at} onChange={(e) => set("ends_at", e.target.value)} />
            </div>
          </div>
          <label className="flex items-center gap-3 text-sm text-white/80">
            <Switch data-testid="banner-active" checked={form.active} onCheckedChange={(v) => set("active", v)} />
            Banner ativo
          </label>
          <div className="flex justify-end gap-3 border-t border-white/10 pt-5">
            <button type="button" onClick={onClose} className="rounded-full border border-white/15 px-6 py-2.5 text-xs uppercase tracking-[0.2em] text-white/70 hover:text-white">
              Cancelar
            </button>
            <button
              type="submit"
              data-testid="banner-save"
              disabled={busy}
              className="inline-flex items-center gap-2 rounded-full bg-gold px-6 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-black hover:bg-gold-light disabled:opacity-60"
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              Guardar
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function AdminBanners() {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null); // null | {} (novo) | banner
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/admin/banners");
      setItems(r.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
      setItems([]);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const toggle = async (b, active) => {
    setBusyId(b.id);
    setItems((cur) => cur.map((x) => (x.id === b.id ? { ...x, active } : x)));
    try {
      await api.patch(`/admin/banners/${b.id}`, { active });
      toast.success(active ? "Banner ativado" : "Banner desativado");
    } catch (e) {
      setItems((cur) => cur.map((x) => (x.id === b.id ? { ...x, active: !active } : x)));
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  const move = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[idx], next[j]] = [next[j], next[idx]];
    setItems(next);
    try {
      await api.put("/admin/banners-reorder", { ids: next.map((b) => b.id) });
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
      load();
    }
  };

  const resetClicks = async (b) => {
    if (!window.confirm("Repor a contagem de cliques deste banner a zero?")) return;
    setBusyId(b.id);
    try {
      await api.post(`/admin/banners/${b.id}/reset-clicks`);
      setItems((cur) => cur.map((x) => (x.id === b.id ? { ...x, clicks_total: 0, clicks: {}, clicks_7d: 0, last_click_at: null } : x)));
      toast.success("Contagem reposta");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (b) => {
    if (!window.confirm("Apagar este banner?")) return;
    setBusyId(b.id);
    try {
      await api.delete(`/admin/banners/${b.id}`);
      setItems((cur) => cur.filter((x) => x.id !== b.id));
      toast.success("Banner apagado");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  if (items === null)
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-gold" />
      </div>
    );

  return (
    <div data-testid="admin-banners-page">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl text-white">Banners promocionais</h1>
          <p className="mt-1 text-sm text-white/50">
            Ative ou desative promoções. O botão &quot;Saber mais&quot; abre o WhatsApp com a mensagem da promoção.
          </p>
        </div>
        <button
          data-testid="banner-new"
          onClick={() => setEditing({})}
          className="inline-flex items-center gap-2 rounded-full bg-gold px-6 py-2.5 text-xs font-semibold uppercase tracking-[0.25em] text-black transition hover:bg-gold-light"
        >
          <Plus className="h-4 w-4" /> Novo banner
        </button>
      </div>

      {items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-white/15 bg-white/[0.02] p-16 text-center">
          <Megaphone className="mx-auto h-12 w-12 text-white/30" strokeWidth={1.2} />
          <p className="mt-4 text-sm text-white/60">Ainda não há banners. Crie o primeiro acima.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((b, i) => {
            const st = statusOf(b);
            return (
              <div
                key={b.id}
                data-testid={`banner-row-${b.id}`}
                className={`flex flex-col gap-4 rounded-2xl border bg-white/[0.03] p-4 sm:flex-row sm:items-center ${
                  b.active ? "border-white/10" : "border-white/5 opacity-70"
                }`}
              >
                <div className="flex h-24 w-full shrink-0 items-center justify-center overflow-hidden rounded-xl bg-black/50 sm:w-40">
                  {b.image || b.image_mobile ? (
                    <img src={imgUrl(b.image || b.image_mobile)} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <Megaphone className="h-8 w-8 text-gold/60" strokeWidth={1.2} />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`rounded-full px-2.5 py-0.5 text-[10px] uppercase tracking-widest ${st.cls}`} data-testid={`banner-status-${b.id}`}>
                      {st.text}
                    </span>
                    {(b.placements || []).map((p) => (
                      <span key={p} className="rounded-full border border-gold/25 px-2.5 py-0.5 text-[10px] uppercase tracking-widest text-gold/80">
                        {placementLabel[p] || p}
                      </span>
                    ))}
                  </div>
                  <p className="mt-2 truncate font-display text-xl text-white">
                    {b.title || "(sem título)"}
                    {(b.image || b.image_mobile) && b.show_text === false && (
                      <span className="ml-2 align-middle text-[10px] uppercase tracking-widest text-white/40">· texto na imagem</span>
                    )}
                  </p>
                  {b.message && <p className="line-clamp-2 text-sm text-white/60">{b.message}</p>}
                  <div
                    data-testid={`banner-clicks-${b.id}`}
                    className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-white/60"
                  >
                    <span className="inline-flex items-center gap-1.5 font-semibold text-gold">
                      <MousePointerClick className="h-3.5 w-3.5" />
                      <span data-testid={`banner-clicks-total-${b.id}`}>{b.clicks_total || 0}</span>
                      {(b.clicks_total || 0) === 1 ? "clique" : "cliques"} em &quot;{b.button_label || "Saber mais"}&quot;
                    </span>
                    <span>· últimos 7 dias: {b.clicks_7d || 0}</span>
                    {["home", "topbar", "popup"].map((p) =>
                      b.clicks?.[p] ? (
                        <span key={p} className="text-white/45">
                          · {placementLabel[p]}: {b.clicks[p]}
                        </span>
                      ) : null,
                    )}
                    {b.last_click_at && (
                      <span className="text-white/40">
                        · último: {new Date(b.last_click_at).toLocaleString("pt-PT", { dateStyle: "short", timeStyle: "short" })}
                      </span>
                    )}
                    {(b.clicks_total || 0) > 0 && (
                      <button
                        type="button"
                        data-testid={`banner-reset-clicks-${b.id}`}
                        onClick={() => resetClicks(b)}
                        disabled={busyId === b.id}
                        className="inline-flex items-center gap-1 text-white/40 transition hover:text-gold"
                      >
                        <RotateCcw className="h-3 w-3" /> repor
                      </button>
                    )}
                  </div>
                  {(b.starts_at || b.ends_at) && (
                    <p className="mt-1 inline-flex items-center gap-1.5 text-xs text-white/45">
                      <CalendarDays className="h-3.5 w-3.5" />
                      {b.starts_at ? `de ${fmtDate(b.starts_at)} ` : ""}
                      {b.ends_at ? `até ${fmtDate(b.ends_at)}` : "sem data de fim"}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1 sm:gap-2">
                  <label className="mr-2 flex items-center gap-2 text-xs text-white/60">
                    <Switch
                      data-testid={`banner-toggle-${b.id}`}
                      checked={!!b.active}
                      disabled={busyId === b.id}
                      onCheckedChange={(v) => toggle(b, v)}
                    />
                    {b.active ? "Ativo" : "Inativo"}
                  </label>
                  <button title="Subir" disabled={i === 0} onClick={() => move(i, -1)} className="flex h-8 w-8 items-center justify-center rounded-full text-white/60 hover:bg-white/10 hover:text-white disabled:opacity-30">
                    <ArrowUp className="h-4 w-4" strokeWidth={1.5} />
                  </button>
                  <button title="Descer" disabled={i === items.length - 1} onClick={() => move(i, 1)} className="flex h-8 w-8 items-center justify-center rounded-full text-white/60 hover:bg-white/10 hover:text-white disabled:opacity-30">
                    <ArrowDown className="h-4 w-4" strokeWidth={1.5} />
                  </button>
                  <button title="Editar" data-testid={`banner-edit-${b.id}`} onClick={() => setEditing(b)} className="flex h-8 w-8 items-center justify-center rounded-full text-white/60 hover:bg-white/10 hover:text-gold">
                    <Pencil className="h-4 w-4" strokeWidth={1.5} />
                  </button>
                  <button title="Apagar" data-testid={`banner-delete-${b.id}`} disabled={busyId === b.id} onClick={() => remove(b)} className="flex h-8 w-8 items-center justify-center rounded-full text-red-300/80 hover:bg-red-500/10 hover:text-red-200 disabled:opacity-40">
                    <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <BannerForm
          banner={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </div>
  );
}
